// Reading the recognition analytics payload.
//
// Three of these fields were added after the Insights tab shipped, and prod has
// run a backend older than the frontend more than once. So every new field is
// read through a fallback here rather than at the point of render, and the
// fallbacks are tested — a panel that renders "NaN%" or a silent 0 is worse
// than one that renders the older, narrower number and says so.
import type { RecognitionAnalytics, RecognitionSourceCount } from '@/services/recognitionService';

/**
 * The share of recognition given AT the work, from any surface.
 *
 * `contextual_recognition_rate` counts work-sourced recognition only. Once
 * recognition could be given from a conversation, that number stopped meaning
 * "in the flow of work" — it is kept for compatibility, and used here only when
 * the backend is too old to send the real one.
 */
export function inFlowRate(d: RecognitionAnalytics): number {
  const rate = d.in_flow_recognition_rate ?? d.contextual_recognition_rate ?? 0;
  // A rate outside 0..1 is a backend bug, but clamping beats rendering "1300%".
  return Math.min(1, Math.max(0, rate));
}

/** True when the figure above is the narrow, work-only one. The UI says so. */
export function inFlowIsWorkOnly(d: RecognitionAnalytics): boolean {
  return d.in_flow_recognition_rate === undefined;
}

export function pct(rate: number): number {
  return Math.round(rate * 100);
}

/**
 * Where recognition came from, biggest first.
 *
 * An older backend sends no breakdown, only `from_work` / `from_other`. Rather
 * than hide the panel, we show those two — which is what the tab used to imply
 * anyway, just stated plainly instead of as one percentage.
 */
export function sourceBuckets(d: RecognitionAnalytics): RecognitionSourceCount[] {
  if (d.by_source && d.by_source.length > 0) return d.by_source;
  const out: RecognitionSourceCount[] = [];
  if (d.from_work > 0) out.push({ key: 'work', label: 'Work items', count: d.from_work, in_flow: true });
  if (d.from_other > 0) out.push({ key: 'other', label: 'Everywhere else', count: d.from_other, in_flow: false });
  return out.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/**
 * The share of recognition the AI helped draft, or null when the backend does
 * not report it.
 *
 * Null rather than 0: "the AI helped with none of it" and "we cannot tell" are
 * different facts, and showing 0% for the second would be a lie about a feature
 * that is working.
 */
export function aiAssistRate(d: RecognitionAnalytics): number | null {
  if (d.ai_suggested_rate === undefined) return null;
  return Math.min(1, Math.max(0, d.ai_suggested_rate));
}
